<div class="historic-epod-container">
  <!-- Loading State (shown only while generating OTP) -->
  <div *ngIf="loading && !showOTPModal" class="loading-container">
    <div class="spinner"></div>
    <p>Generating OTP...</p>
  </div>

  <!-- Error State (shown only if OTP generation fails) -->
  <div *ngIf="error && !showOTPModal && !showHistoricReportModal" class="error-container">
    <div class="error-icon">⚠️</div>
    <p class="error-message">{{ error }}</p>
  </div>

  <!-- OTP Modal -->
  <ng-container *ngIf="showOTPModal">
    <div class="otp-modal-overlay" (click)="isVerified ? closeOTPModal() : null">
      <div class="otp-modal" (click)="$event.stopPropagation()">
        <div class="otp-modal-header">
          <h3>OTP Verification</h3>
          <button type="button" class="close-btn" (click)="isVerified ? closeOTPModal() : null" *ngIf="isVerified">×</button>
        </div>
        <div class="otp-modal-body">
          <p class="modal-description">OTP sent to <strong>{{ email }}</strong></p>
          
          <!-- OTP Verification Section -->
          <div class="otp-verification-section" *ngIf="!isVerified">
            <p class="verification-label">Enter 6-digit OTP:</p>
            <div class="otp-input-group">
              <input 
                type="text" 
                [(ngModel)]="otpInput" 
                placeholder="Enter 6-digit OTP"
                maxlength="6"
                class="otp-input"
                (keyup)="onOTPInput($event)"
                [disabled]="verifyingOTP"
                autocomplete="off"
                inputmode="numeric">
            </div>
            <div class="otp-error" *ngIf="otpError">
              <span class="error-icon">⚠️</span>
              {{ otpError }}
            </div>
          </div>

          <!-- Success Message -->
          <div class="verification-success" *ngIf="isVerified">
            <div class="success-icon">✓</div>
            <p class="success-message">OTP verified successfully!</p>
          </div>
        </div>
        <div class="otp-modal-footer">
          <button 
            type="button" 
            class="btn-secondary" 
            (click)="resendOTP()"
            [disabled]="verifyingOTP || loading"
            *ngIf="!isVerified">
            Resend OTP
          </button>
          <button 
            type="button" 
            class="btn-primary" 
            (click)="verifyOTP()"
            [disabled]="verifyingOTP || !otpInput || otpInput.length !== 6 || isVerified"
            *ngIf="!isVerified">
            <span *ngIf="!verifyingOTP">Submit</span>
            <span *ngIf="verifyingOTP">Verifying...</span>
          </button>
          <button 
            type="button" 
            class="btn-primary" 
            (click)="closeOTPModal()"
            *ngIf="isVerified">
            Continue
          </button>
        </div>
      </div>
    </div>
  </ng-container>

  <!-- Historic Report Modal -->
  <ng-container *ngIf="showHistoricReportModal">
    <div class="otp-modal-overlay" (click)="closeHistoricReportModal()">
      <div class="otp-modal historic-report-modal" (click)="$event.stopPropagation()">
        <div class="otp-modal-header">
          <h3>Download Historic Report</h3>
          <button type="button" class="close-btn" (click)="closeHistoricReportModal()" [disabled]="isDownloadingReport">×</button>
        </div>
        <div class="otp-modal-body">
          <p class="modal-description">Select a date range to download the historic report. Maximum date range allowed is 92 days.</p>
          
          <div class="date-range-fields" [formGroup]="historicReportForm">
            <mat-form-field appearance="outline" class="date-field">
              <mat-label>Start Date</mat-label>
              <input matInput 
                formControlName="startDate"
                [matDatepicker]="startDatePicker" 
                [max]="currentDate"
                (dateChange)="onStartDateChange($event)"
                placeholder="Choose Start Date"
                readonly
                (click)="startDatePicker.open()"
                required>
              <mat-datepicker-toggle matSuffix [for]="startDatePicker">
                <img matDatepickerToggleIcon class="blue"
                  src="https://console.freightfox.ai/assets/images/calender-icon.svg">
                <img matDatepickerToggleIcon class="grey"
                  src="https://console.freightfox.ai/assets/images/calendar-grey.svg">
              </mat-datepicker-toggle>
              <mat-datepicker #startDatePicker></mat-datepicker>
            </mat-form-field>

            <mat-form-field appearance="outline" class="date-field">
              <mat-label>End Date</mat-label>
              <input matInput 
                formControlName="endDate"
                [matDatepicker]="endDatePicker" 
                [max]="getMaxEndDate()"
                [min]="historicReportForm.get('startDate')?.value"
                (dateChange)="onEndDateChange($event)"
                placeholder="Choose End Date"
                readonly
                (click)="endDatePicker.open()"
                required>
              <mat-datepicker-toggle matSuffix [for]="endDatePicker">
                <img matDatepickerToggleIcon class="blue"
                  src="https://console.freightfox.ai/assets/images/calender-icon.svg">
                <img matDatepickerToggleIcon class="grey"
                  src="https://console.freightfox.ai/assets/images/calendar-grey.svg">
              </mat-datepicker-toggle>
              <mat-datepicker #endDatePicker></mat-datepicker>
            </mat-form-field>
          </div>

          <div *ngIf="dateRangeError" class="error-message">
            <span class="error-icon">⚠️</span>
            {{ dateRangeError }}
          </div>

          <div *ngIf="isDownloadingReport" class="download-progress">
            <div class="progress-spinner"></div>
            <p class="progress-text">{{ downloadProgress }}</p>
          </div>
        </div>
        
        <div class="otp-modal-footer">
          <button 
            type="button" 
            class="btn-secondary" 
            (click)="closeHistoricReportModal()"
            [disabled]="isDownloadingReport">
            Cancel
          </button>
          <button 
            type="button" 
            class="btn-primary" 
            (click)="downloadHistoricReport()"
            [disabled]="!canDownloadReport()">
            <span *ngIf="!isDownloadingReport">Download Report</span>
            <span *ngIf="isDownloadingReport">Downloading...</span>
          </button>
        </div>
      </div>
    </div>
  </ng-container>

  <!-- Download Success Modal -->
  <ng-container *ngIf="showDownloadSuccess">
    <div class="otp-modal-overlay" (click)="closeSuccessModal()">
      <div class="otp-modal success-modal" (click)="$event.stopPropagation()">
        <div class="otp-modal-header success-header">
          <h3>Download Complete</h3>
          <button type="button" class="close-btn" (click)="closeSuccessModal()">×</button>
        </div>
        <div class="otp-modal-body">
          <div class="download-success-content">
            <div class="success-icon-large">✓</div>
            <h4 class="success-title">Report Downloaded Successfully!</h4>
            <div class="success-details">
              <div class="detail-row">
                <span class="detail-label">Date Range:</span>
                <span class="detail-value">{{ downloadedDateRange }}</span>
              </div>
            </div>
            <p class="success-note">The Excel file has been downloaded to your device.</p>
          </div>
        </div>
        <div class="otp-modal-footer success-footer">
          <button 
            type="button" 
            class="btn-secondary" 
            (click)="downloadAnotherReport()">
            Download Another Report
          </button>
          <button 
            type="button" 
            class="btn-primary" 
            (click)="closeSuccessModal()">
            Done
          </button>
        </div>
      </div>
    </div>
  </ng-container>
</div>
